
import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from '../../styles/AsignacionBecasStyles/AsignacionBecas.module.css';
import { getStudentsPaged } from '../../api/students';
import Pagination from '../../components/Pagination.tsx';
import LoaderOverlay from '../../components/LoaderOverlay';

function StudentTable({ onStudentClick, selectedCampus, selectedCiclo, searchTerm, onLoading }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const onLoadingRef = useRef(onLoading);
  useEffect(() => { onLoadingRef.current = onLoading; }, [onLoading]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    setPage(1); // Reset page when filters/search change
  }, [selectedCampus, selectedCiclo, searchTerm]);

  const loadStudents = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (onLoadingRef.current) onLoadingRef.current(true);
    try {
      const params = {
        ordering: 'nombre_completo',
        page,
        page_size: pageSize
      };
      if (selectedCampus) params.id_campus = selectedCampus;
      if (selectedCiclo) params.id_ciclo_escolar = selectedCiclo;
      if (searchTerm) params.search = searchTerm;
      const data = await getStudentsPaged(params);
      const studentsArray = Array.isArray(data.results) ? data.results : (data.results || []);
      setStudents(studentsArray);
      setTotal(data.count || 0);
    } catch (error) {
      console.error('Error al cargar estudiantes:', error);
      setStudents([]);
      setTotal(0);
      setError('No fue posible cargar estudiantes con los filtros seleccionados.');
    } finally {
      setLoading(false);
      if (onLoadingRef.current) onLoadingRef.current(false);
    }
  }, [selectedCampus, selectedCiclo, searchTerm, page, pageSize]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  useEffect(() => {
    const handleInscripcionesUpdated = () => {
      loadStudents();
    };

    window.addEventListener('inscripciones:updated', handleInscripcionesUpdated);
    return () => {
      window.removeEventListener('inscripciones:updated', handleInscripcionesUpdated);
    };
  }, [loadStudents]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const getSeccionNombre = (student) => {
    return student.seccion_nombre || 'N/A';
  };
  return (
    <div className={styles.tableSection} style={{ position: 'relative' }}>
      <div className={styles.tableHeader}>
        <div className={styles.tableIcon}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="#ed8b00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 21V19C6 17.9391 6.42143 16.9217 7.17157 16.1716C7.92172 15.4214 8.93913 15 10 15H14C15.0609 15 16.0783 15.4214 16.8284 16.1716C17.5786 16.9217 18 17.9391 18 19V21" stroke="#ed8b00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h3 className={styles.tableTitle}>Estudiantes</h3>
      </div>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Matrícula</th>
              <th>Campus</th>
              <th>Sección</th>
              <th>Programa</th>
              <th>Ciclo Escolar</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center' }}>
                  {/* El loader cubre la tabla, pero se deja el mensaje por accesibilidad */}
                  Cargando estudiantes...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center' }}>
                  {error}
                </td>
              </tr>
            ) : students.length > 0 ? (
              <>
                {students.map((student, index) => (
                  <tr
                    key={student.id_estudiante || student.matricula}
                    className={index % 2 === 0 ? styles.evenRow : styles.oddRow}
                    onClick={() => onStudentClick(student)}
                  >
                    <td className={styles.nameCell}>{student.nombre_completo}</td>
                    <td>{student.matricula}</td>
                    <td>{student.campus_nombre || 'N/A'}</td>
                    <td>{getSeccionNombre(student)}</td>
                    <td>{student.programa_nombre || 'N/A'}</td>
                    <td>{student.ciclo_actual_nombre || (student.ciclo_actual && student.ciclo_actual.nombre_ciclo) || student.ciclo_escolar_nombre || 'N/A'}</td>
                  </tr>
                ))}
                {Array.from({ length: Math.max(0, pageSize - students.length) }, (_, index) => (
                  <tr key={`empty-${index}`} className={index % 2 === 0 ? styles.evenRow : styles.oddRow}>
                    <td></td><td></td><td></td><td></td><td></td><td></td>
                  </tr>
                ))}
              </>
            ) : (
              <>
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center' }}>
                    No se encontraron estudiantes
                  </td>
                </tr>
                {Array.from({ length: pageSize - 1 }, (_, index) => (
                  <tr key={`empty-${index}`} className={index % 2 === 0 ? styles.evenRow : styles.oddRow}>
                    <td></td><td></td><td></td><td></td><td></td><td></td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px' }}>
        <span style={{ fontSize: '14px', color: '#898d8d' }}>
          Mostrando {students.length > 0 ? ((page - 1) * pageSize) + 1 : 0}–{Math.min(page * pageSize, total)} de {total} estudiantes
        </span>
        {totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  );
}

export default StudentTable;
